import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { palette } from '@m12s/component-library';
import { ElevatedCard } from 'lib/utils/styled';
import { Loading } from 'components/Loading';

import usePickingSummary from 'lib/hooks/usePickingSummary';
import {
  formatMaterialQty,
  formatMaterialRatio,
} from 'lib/utils/pickingMaterials';
import { D365_SOURCE } from 'lib/selectors/getPickingFieldConfig';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const Scroll = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1.5rem;
  gap: 1rem;
`;

const Empty = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${palette.Grey500};
  font-size: 0.9375rem;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${palette.Grey400};
  border-radius: 0.375rem;
  background: ${palette.White};
  color: ${palette.Grey700};
  font: inherit;
  font-size: 0.875rem;
  cursor: pointer;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${palette.Grey700};
`;

const SectionName = styled.span``;

const SectionSep = styled.span`
  color: ${palette.Grey400};
  font-weight: 400;
`;

const SectionRatio = styled.span``;

const SectionTotal = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.3125rem;
`;

const AVAILABLE_COLORS = {
  warn: palette.Orange600,
  good: palette.Green700,
  neutral: palette.Grey700,
};

const Available = styled.span`
  font-weight: 600;
  color: ${({ tone }) => {
    return AVAILABLE_COLORS[tone] || palette.Green700;
  }};
`;

// Orange when nothing is left, but only for a material D365 enforces: one it
// doesn't (enforced === false) is never short, so it stays neutral.
const availableTone = (mat) => {
  if (mat.enforced === false) return 'neutral';
  return mat.remaining <= 0 ? 'warn' : 'good';
};

const LpRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const LpRow = styled.div`
  ${ElevatedCard}
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 3.5rem;
  padding: 1.25rem;
  background: ${palette.White};
`;

const LpLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
  min-width: 0;
`;

const LpNumber = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${palette.Grey700};
`;

// Marks a pick made (or returned) in D365 rather than in OperatorView.
const SourceTag = styled.span`
  margin-left: 0.5rem;
  padding: 0 0.375rem;
  border: 1px solid ${palette.Grey300};
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${palette.Grey600};
`;

const LpLocation = styled.span`
  font-size: 0.75rem;
  color: ${palette.Grey500};
`;

const NoPicks = styled.div`
  padding: 0 0.25rem;
  font-size: 0.875rem;
  color: ${palette.Grey500};
`;

const LpQtyGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: ${palette.Grey700};
`;

const MaterialsLog = ({ wooId }) => {
  const { materials, picks, loading, error, refetch } = usePickingSummary(
    wooId
  );

  const hasPicks = picks.length > 0;
  // Material rows come from the WOO's custom fields, while picks can also come
  // from Redux (just written, or being written for a job start), so the loading
  // and error states follow the rows: picks alone cannot be shown.
  const hasRows = materials.length > 0;

  // Spinner only while there's nothing to show yet — cache-and-network keeps
  // `loading` true during background refreshes of already-rendered rows.
  if (!hasRows && loading) {
    return (
      <Wrap>
        <Loading text="Loading materials" size="2.5rem" fontSize="0.9375rem" />
      </Wrap>
    );
  }

  // A failed read must not look like "nothing picked", which could lead the
  // operator to pick the same material again.
  if (!hasRows && error) {
    return (
      <Wrap>
        <Empty>
          <span>Materials could not be loaded.</span>
          <RetryButton
            type="button"
            onClick={() => {
              return refetch();
            }}
          >
            Retry
          </RetryButton>
        </Empty>
      </Wrap>
    );
  }

  if (!hasPicks) {
    return (
      <Wrap>
        <Empty>
          <span>No materials picked yet.</span>
          <span>Use &ldquo;Add materials&rdquo; to pick.</span>
        </Empty>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Scroll>
        {/* Once anything is picked every required material is shown, as the
            labor ticket form checks every one of them (useMaterialCoverageContext). */}
        {materials.map((mat) => {
          const matPicks = mat.picks;
          const ratio = formatMaterialRatio(mat.qtyPerUnit);

          return (
            <Section key={mat.itemNumber}>
              <SectionHeader>
                <SectionName>{mat.itemNumber}</SectionName>
                {ratio && (
                  <>
                    <SectionSep>|</SectionSep>
                    <SectionRatio>{ratio}</SectionRatio>
                  </>
                )}
                <SectionTotal>
                  <span>{formatMaterialQty(mat.pickedQty)}</span>
                  <span>picked</span>
                  <SectionSep>&middot;</SectionSep>
                  <Available
                    tone={availableTone(mat)}
                    data-tone={availableTone(mat)}
                  >
                    {formatMaterialQty(mat.remaining)}
                  </Available>
                  <span>available</span>
                </SectionTotal>
              </SectionHeader>
              {matPicks.length === 0 ? (
                <NoPicks>
                  No picks yet. Use &ldquo;Add materials&rdquo;.
                </NoPicks>
              ) : null}
              <LpRows>
                {matPicks.map((pick) => {
                  return (
                    <LpRow key={pick.id}>
                      <LpLabel>
                        <LpNumber>
                          {pick.licensePlate}
                          {pick.source === D365_SOURCE ? (
                            <SourceTag>D365</SourceTag>
                          ) : null}
                        </LpNumber>
                        {pick.location ? (
                          <LpLocation>{pick.location}</LpLocation>
                        ) : null}
                      </LpLabel>
                      <LpQtyGroup>
                        {/* A return made in D365 is a negative pick. */}
                        <span>
                          {pick.quantity < 0
                            ? `Returned ${formatMaterialQty(-pick.quantity)}`
                            : formatMaterialQty(pick.quantity)}
                        </span>
                        <span>{mat.unit}</span>
                      </LpQtyGroup>
                    </LpRow>
                  );
                })}
              </LpRows>
            </Section>
          );
        })}
      </Scroll>
    </Wrap>
  );
};

MaterialsLog.propTypes = {
  // The WOO's workOrderOperationRef; picks are read from Redux under it.
  wooId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

MaterialsLog.defaultProps = {
  wooId: undefined,
};

export default MaterialsLog;
